import { useEffect, useState } from 'react';
import usePrerenderReady from '@/lib/usePrerenderReady';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Kalamanch.art';
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://www.kalamanch.art').replace(/\/$/, '');
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '';
const DEFAULT_TITLE = 'Kalamanch.art | Post. Connect. Get Work.';
const DEFAULT_DESCRIPTION = 'India\'s digital stage for creative talent. Artists can post profiles, photos, videos, and portfolios while collaborators discover verified Indian artists.';
const DEFAULT_KEYWORDS = 'Kalamanch, Kalamanch.art, Indian artists, hire artists, performing artists, singers, dancers, actors, influencers, collaborators, creative talent India';
const DEFAULT_IMAGE = '/images/kalamanch-logo.jpg';
const DEFAULT_IMAGE_ALT = 'Kalamanch.art - Post. Connect. Get Work.';
const SEO_CACHE_VERSION = 'artist-seo-2026-07-02-v6';

const ensureMeta = (selector, createAttributes) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
};

const setMetaContent = (selector, createAttributes, content) => {
  if (!content) return;
  ensureMeta(selector, createAttributes).setAttribute('content', content);
};

const setCanonical = (url) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
};

const setJsonLd = (schema) => {
  const id = 'kalamanch-seo-jsonld';
  let script = document.getElementById(id);
  if (!schema) {
    script?.remove();
    return;
  }
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schema);
};

const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  type = 'website',
  schema,
}) => {
  const { pathname } = useLocation();
  const [loadingSeo, setLoadingSeo] = useState(Boolean(API));
  usePrerenderReady(loadingSeo);

  useEffect(() => {
    let cancelled = false;
    setLoadingSeo(Boolean(API));
    const cacheKey = `kalamanch_seo_${SEO_CACHE_VERSION}_${pathname}`;
    const applySeo = (seo = {}) => {
      if (cancelled) return;
      const nextTitle = seo.title || title;
      const nextDescription = seo.description || description;
      const nextKeywords = seo.keywords || keywords;
      const nextImage = seo.image_url || image;
      const nextImageAlt = seo.image_alt_text || seo.image_alt || imageAlt || DEFAULT_IMAGE_ALT;
      const nextImageTitle = seo.image_title || nextTitle;
      const nextImageCaption = seo.image_caption || nextDescription;
      const nextOgTitle = seo.og_title || nextTitle;
      const nextOgDescription = seo.og_description || nextDescription;
      const nextSchema = seo.schema || schema;
      const canonicalUrl = `${SITE_URL}${pathname}`;
      const absoluteImage = nextImage?.startsWith('http') ? nextImage : `${SITE_URL}${nextImage || DEFAULT_IMAGE}`;
      const pageTitle = nextTitle.includes(SITE_NAME) ? nextTitle : `${nextTitle} | ${SITE_NAME}`;
      const ogPageTitle = nextOgTitle.includes(SITE_NAME) ? nextOgTitle : `${nextOgTitle} | ${SITE_NAME}`;

      document.title = pageTitle;
      setCanonical(canonicalUrl);
      setMetaContent('meta[name="description"]', { name: 'description' }, nextDescription);
      setMetaContent('meta[name="keywords"]', { name: 'keywords' }, nextKeywords);
      setMetaContent('meta[name="image:title"]', { name: 'image:title' }, nextImageTitle);
      setMetaContent('meta[name="image:caption"]', { name: 'image:caption' }, nextImageCaption);
      setMetaContent('meta[name="robots"]', { name: 'robots' }, 'index, follow');
      setMetaContent('meta[property="og:title"]', { property: 'og:title' }, ogPageTitle);
      setMetaContent('meta[property="og:description"]', { property: 'og:description' }, nextOgDescription);
      setMetaContent('meta[property="og:type"]', { property: 'og:type' }, type);
      setMetaContent('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
      setMetaContent('meta[property="og:image"]', { property: 'og:image' }, absoluteImage);
      setMetaContent('meta[property="og:image:secure_url"]', { property: 'og:image:secure_url' }, absoluteImage);
      setMetaContent('meta[property="og:image:alt"]', { property: 'og:image:alt' }, nextImageAlt);
      setMetaContent('meta[property="og:locale"]', { property: 'og:locale' }, 'en_IN');
      setMetaContent('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME);
      setMetaContent('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
      setMetaContent('meta[name="twitter:title"]', { name: 'twitter:title' }, ogPageTitle);
      setMetaContent('meta[name="twitter:description"]', { name: 'twitter:description' }, nextOgDescription);
      setMetaContent('meta[name="twitter:image"]', { name: 'twitter:image' }, absoluteImage);
      setMetaContent('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, nextImageAlt);
      setJsonLd(nextSchema);
    };

    const cachedSeo = (() => {
      try {
        return JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null');
      } catch {
        return null;
      }
    })();

    if (cachedSeo) {
      applySeo(cachedSeo);
    } else if (!API) {
      applySeo();
    }

    if (API) {
      fetch(`${API}/seo?path=${encodeURIComponent(pathname)}`, { cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .then((payload) => {
          if (payload?.seo) {
            try {
              window.sessionStorage.setItem(cacheKey, JSON.stringify(payload.seo));
            } catch {
              // Ignore storage failures; SEO still applies for the current view.
            }
            applySeo(payload.seo);
          } else if (!cachedSeo) {
            applySeo();
          }
        })
        .catch(() => {
          if (!cachedSeo) applySeo();
        })
        .finally(() => { if (!cancelled) setLoadingSeo(false); });
    }

    return () => {
      cancelled = true;
    };
  }, [description, image, imageAlt, keywords, pathname, schema, title, type]);

  return null;
};

export default SEO;
